import {ACCOUNT_TYPE_LABEL_MAP, AREA_OF_FRAUD_LABEL_MAP} from "../../../api/cases";
import { PARTY_TYPE_LABELS } from "../../../api/PartiesInvolved";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";

import {updateCaseFields,validateBackground} from "../logic/validations";

import {OverviewItem} from "../templates/helper";
import FraudAmountOverviewItem from "./FraudAmountOverviewItem";

import { BookCheck, BookCheckIcon, Building, CalendarDays, FolderGit, Hash, MapPin, MapPinnedIcon, ReceiptIndianRupee, SaveIcon, SquarePen, SquareUserRound, TextSearch, User, UserPlus, Users, X } from "lucide-react";

import ButtonV2 from "../../../../src/components/ui/button/v2";
import { TextareaV2 } from "../../../components/ui/textbox/v2";
import { cn } from "../../../lib/utils";

import {selectCaseHeader,selectEditableField,selectCaseHeaderIsUpdating,setEditableField,cancelEditMode, setEditMode, isCreditAreaOfFraud} from "../logic/Slice";

import { updateCase } from "../../../thunks/Case";
import { fetchAnalysisStepsData } from "../../../thunks/FullCaseData";
import { fetchEnrichedStakeholder } from "../../stakeholder/logic/stakeholderThunks";

import { useAppDispatch, useAppSelector } from "../../../app/hooks";
import { showErrorToast } from "../../../lib/toast";
import { EntityPopover } from "../../../components/ui/popover/EntityPopover";
import CreditAccountsModal from "../../creditAccounts/components/CreditAccountsModal";
import AccountsModal from "../../accounts/components/AccountsModal";
import { AccountsPopover } from "../../accounts/components/AccountsPopover";
import { getCaseAccounts } from "../../accounts/logic/slice";
import AddEmployeeModal from "../../workbench/components/AddEmployeeModal";
import AddPartyModal from "../../workbench/components/AddPartyModal";
import AddExistingStakeholderAsParty from "../../workbench/components/AddExistingStakeholderAsParty";
import EntityPopoverRowWithBadge from "../../workbench/components/EntityPopoverRow";
import { selectAllPartiesInvolved } from "../../partiesinvolved/logic/PartiesInvolvedSelectors";
import { getAccountProductInfo } from "../../creditAccounts/logic/slice";
import { CrediAccountsPopover } from "../../creditAccounts/components/CreditAccountsPopover";
import { isCreditAccountStakeholdersRevampEnabled, isOperaionalCyberRevampEnabled } from "../../configs/logic/Slice";
import { getPartiesInvolved, getEmployeesInvolved } from "../../stakeholder/logic/stakeholderSlice";

export const CaseOverviewCard: React.FC = () => {

  const dispatch = useAppDispatch();
  const { id: caseIdFromParams } = useParams<{ id: string }>();

  const summaryData = useAppSelector(selectCaseHeader);
  const caseId = caseIdFromParams || summaryData?.case_id;

  const editableField = useAppSelector(selectEditableField);
  const isSaving = useAppSelector(selectCaseHeaderIsUpdating);
  const partiesInvolved = useAppSelector(selectAllPartiesInvolved);
  const isCreditStakeholderRevampEnabled = useAppSelector(isCreditAccountStakeholdersRevampEnabled);
  const isCyberOpsRevampEnabled = useAppSelector(isOperaionalCyberRevampEnabled);

  const allStakeholderParties = useAppSelector(getPartiesInvolved);
  const allStakeholderEmployees = useAppSelector(getEmployeesInvolved);

  const partiesInvolvedRef = useRef<HTMLInputElement>(null);

  const isEditing = (field: string) => editableField === field;

  const loanAccountInfo = useAppSelector(getAccountProductInfo);
  const caseAccounts = useAppSelector(getCaseAccounts);

  const isCreditFraud = useAppSelector(isCreditAreaOfFraud);

  const loanReferenceIds =
    (loanAccountInfo|| []).map(
      item => `${item.ext_account_number} (${item.product})`
    );

  const accountReferenceIds = caseAccounts.map(
    (item) => `${item.ext_account_number} (${item.account_name})`,
  );

  const employeeList = summaryData?.employee_ids || [];

  const partiesInvolvedString =
    (partiesInvolved)
      .map(party => party.name)
      .join(", ") || "";

  const partyDisplayItems = partiesInvolved.map(
    party =>
      `${party.name} (${
        PARTY_TYPE_LABELS[
          party.party_type as keyof typeof PARTY_TYPE_LABELS
        ] ?? party.party_type
      })`
  );    

  const stakeholderParties = isCreditStakeholderRevampEnabled ? allStakeholderParties : [];
  const stakeholderEmployees = isCreditStakeholderRevampEnabled ? allStakeholderEmployees : [];

  const employeeStakeholderIds = useMemo(() => {
    if (!isCreditStakeholderRevampEnabled) return [];
    return stakeholderEmployees.map((e) => e.stakeholderId);
  }, [isCreditStakeholderRevampEnabled, stakeholderEmployees]);

  const employeeDisplayString = useMemo(
    () =>
      isCreditStakeholderRevampEnabled
        ? allStakeholderEmployees.map((e) => `${e.name} (${e.employeeId})`).join(", ")
        : employeeList.join(", "),
    [isCreditStakeholderRevampEnabled, allStakeholderEmployees, employeeList]
  );

  useEffect(() => {
    if (!caseId || employeeStakeholderIds.length === 0) {
      return;
    }
    dispatch(
      fetchEnrichedStakeholder({
        caseId: caseId,
        stakeholderIds: employeeStakeholderIds,
      })
    );
  }, [caseId, dispatch, employeeStakeholderIds]);


  const [backgroundValue, setBackgroundValue] = useState(summaryData?.background || "");
  const [backgroundError, setBackgroundError] = useState<string | null>(null);

  const [isEditingParties, setIsEditingParties] = useState(false);
  const [partiesInvolvedError, setPartiesInvolvedError] = useState<string | null>(null);

  useEffect(() => {
    setBackgroundValue(summaryData?.background || "");
    if (!isEditing(updateCaseFields.BACKGROUND)) setBackgroundError(null);
  }, [editableField, summaryData?.background]);

  useEffect(() => {
    if (isEditingParties) partiesInvolvedRef.current?.focus();
    else setPartiesInvolvedError(null);
  }, [isEditingParties]);

  const getChangedField = (field: string, value: any) => {

    if (field === updateCaseFields.EXPOSURE) {
      const exposure = Number(value);

      if (Number(summaryData?.exposure ?? 0) === exposure) {
        return null;
      }

      return { exposure };
    }

    if (field === updateCaseFields.EMPLOYEE) {
    const existing = Array.isArray(summaryData?.employee_ids)
      ? summaryData.employee_ids
      : [];

    const updated = [...existing, value];

    if (JSON.stringify(existing) !== JSON.stringify(updated)) {
      return { employee_ids: updated };
    }

    return null;
   }

    if (field === updateCaseFields.LOAN_PRODUCT_INFO) {
    const existing = Array.isArray(summaryData?.loan_product_info)
      ? summaryData.loan_product_info
      : [];

    const updated = [...existing, value];

    if (JSON.stringify(existing) !== JSON.stringify(updated)) {
      return { loan_product_info: updated };
    }

    return null;
  }

    if ((summaryData as any)?.[field] !== value) {
      return { [field]: value };
    }

    return null;
  };

  const saveField = async (field: string, value: any) => {

    const changedField = getChangedField(field, value);
    if (!changedField || !summaryData?.case_id) {
      return true;
    }

    const resultAction = await dispatch(
      updateCase({
        caseId: summaryData.case_id,
        request: changedField
      })
    );

    if (updateCase.fulfilled.match(resultAction)) {
      if (field === updateCaseFields.LOAN_PRODUCT_INFO) {
        dispatch(fetchAnalysisStepsData({ caseId: summaryData.case_id }));
      }
      dispatch(cancelEditMode());
      return true;

    } else {
      console.error("Update failed:", resultAction.payload);
      showErrorToast("Failed to update. Try again.");
      return false;
    }
  };

  const handleBackgroundChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setBackgroundError(validateBackground(e.target.value));
    setBackgroundValue(e.target.value);
  };

  const handleBackgroundSave = () => {
    const error = validateBackground(backgroundValue);
    setBackgroundError(error);
    if (!error) saveField(updateCaseFields.BACKGROUND, backgroundValue);
  };

  const InvestigationSection = React.useMemo(() => (

    <div className="space-y-4">

      <span className="text-xs font-normal leading-4 text-[#0A0A0A] font-sans">
        Investigation Overview
      </span>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-4">

        <OverviewItem icon={<Hash />} label="UIN" value={summaryData?.uin} />
        <OverviewItem icon={<MapPin />} label="Region" value={summaryData?.region} />
        <OverviewItem icon={<MapPinnedIcon />} label="Zone" value={summaryData?.zone} />
        <OverviewItem icon={<Building />} label="Branch" value={summaryData?.branch} />
        <OverviewItem icon={<FolderGit />} label="Source" value={summaryData?.source} />
        <OverviewItem
          icon={<TextSearch />}
          label="Nature of Suspicion"
          displayValue={summaryData?.investigation_nature?.join(", ")}
          popoverContent={
            summaryData?.investigation_nature?.length ? (
              <EntityPopover
                icon={<BookCheck width={16} />}
                title="Nature of Suspicion"
                allowCopy
                items={summaryData.investigation_nature.map(i => i.trim())}
              />
            ) : undefined
          }
        />
        <FraudAmountOverviewItem
          field={updateCaseFields.EXPOSURE}
          onSave={(exposure) => saveField(updateCaseFields.EXPOSURE, exposure)}
        />
        <OverviewItem icon={<CalendarDays />} label="Referral Date" value={summaryData?.referral_date} />
        <OverviewItem icon={<UserPlus />} label="Referrer" value={summaryData?.referrer} />
        <OverviewItem
          icon={<ReceiptIndianRupee />}
          label="Area of Fraud"
          value={summaryData?.area_of_fraud ? AREA_OF_FRAUD_LABEL_MAP[summaryData.area_of_fraud]: "N/A"}
        />
      </div>
    </div>

  ), [summaryData, editableField, isSaving, saveField]);

  const BackgroundSection = React.useMemo(() => {

    const backgroundSizeClassReadonly = "min-h-16 max-h-[min(24rem,55vh)]";
    const backgroundSizeClassEditable =
      "min-h-16 max-h-[min(24rem,55vh)] h-auto [field-sizing:content] resize-y overflow-y-auto overflow-x-hidden";
    const isBackgroundEditable =
      isEditing(updateCaseFields.BACKGROUND) && !isSaving;

    return (
      <div className="space-y-1 group">

        <div className="flex items-center gap-2 justify-between">

          <span className="text-xs font-normal leading-4 text-[#0A0A0A] font-sans">
            Brief Background
          </span>

          <div className="flex items-center gap-1">

            {isSaving && isEditing(updateCaseFields.BACKGROUND) ? (

              <span className="text-xs text-gray-500 italic">Saving...</span>

            ) : !isEditing(updateCaseFields.BACKGROUND) ? (

              <ButtonV2
                variant="ghost"
                className="p-0 w-[28px] h-[28px] bg-slate-200 opacity-0 group-hover:opacity-100 transition-opacity"
                onClick={() => {dispatch(setEditableField(updateCaseFields.BACKGROUND)); dispatch(setEditMode(true));}}
                prefix={<SquarePen width={16} />}
              />

            ) : (

              <>
                <ButtonV2
                  variant="ghost"
                  className="p-0 w-[28px] h-[28px] bg-slate-200"
                  onClick={handleBackgroundSave}
                  prefix={<SaveIcon width={16} />}
                />

                <ButtonV2
                  variant="ghost"
                  className="p-0 w-[28px] h-[28px]"
                  onClick={() => dispatch(cancelEditMode())}
                  prefix={<X width={16} />}
                />
              </>

            )}

          </div>

        </div>

        {isBackgroundEditable ? (
          <TextareaV2
            name="background"
            value={backgroundValue}
            onChange={handleBackgroundChange}
            placeholder="Add background information here..."
            className={cn(
              "w-full min-w-0 max-w-full box-border",
              backgroundSizeClassEditable,
              "border border-gray-200 rounded-lg p-1 text-sm shadow-none",
              "focus:ring-0 focus:outline-none"
            )}
          />
        ) : (
          <div
            className={cn(
              "min-w-0 w-full max-w-full rounded-lg border border-gray-200 box-border p-1 text-sm",
              "bg-background text-foreground overflow-y-auto overflow-x-hidden",
              backgroundSizeClassReadonly
            )}
            role="textbox"
            aria-readonly="true"
            aria-multiline="true"
            tabIndex={0}
          >
            {backgroundValue ? (
              <span className="whitespace-pre-wrap break-words">{backgroundValue}</span>
            ) : (
              <span className="text-muted-foreground/60">
                Add background information here...
              </span>
            )}
          </div>
        )}

        {backgroundError && (
          <p className="mt-1 text-xs text-red-600">{backgroundError}</p>
        )}

      </div>
    );
  }, [backgroundValue, backgroundError, editableField, isSaving]);

  const AccountEmployeeSection = React.useMemo(() => (

    <div className="space-y-3">

      <span className="text-xs font-normal leading-4 text-[#0A0A0A] font-sans">
        Account & Employee References
      </span>

      <div className="space-y-4">

        <OverviewItem
          icon={<SquareUserRound />}
          label="Account Type"
          value={ summaryData?.account_type? ACCOUNT_TYPE_LABEL_MAP[summaryData.account_type]: "N/A"}
        />
        {isCreditFraud ? (
          <OverviewItem
            icon={<BookCheckIcon />}
            label="Loan Accounts"
            value={loanReferenceIds.join(", ")}
            popoverContent={
              !isEditing(updateCaseFields.LOAN_PRODUCT_INFO) && loanAccountInfo.length ? (
                <CrediAccountsPopover creditAccounts={loanAccountInfo} />
              ) : undefined
            }
            isEditable
            field={updateCaseFields.LOAN_PRODUCT_INFO}
          />
        ) : isCyberOpsRevampEnabled ? (
          <OverviewItem
            icon={<BookCheckIcon />}
            label="Accounts"
            value={accountReferenceIds.join(", ")}
            popoverContent={
              !isEditing(updateCaseFields.ACCOUNTS) && caseAccounts.length ? (
                <AccountsPopover accounts={caseAccounts} />
              ) : undefined
            }
            isEditable
            field={updateCaseFields.ACCOUNTS}
          />
        ) : null}
        <OverviewItem
          key={employeeDisplayString}
          icon={<User />}
          label="Employee ID"
          value={employeeDisplayString}
          popoverContent={
            isCreditStakeholderRevampEnabled ? (
              stakeholderEmployees.length ? (
                <EntityPopover
                  icon={<User width={16} />}
                  title="Employee"
                  allowCopy
                  items={stakeholderEmployees.map((employee, index) => ({
                    content: (
                      <EntityPopoverRowWithBadge
                        title={employee.name}
                        badge={employee.relatedAs}
                        secondaryText={employee.employeeId}
                        showDivider={index > 0}
                      />
                    ),
                    copyValue: employee.employeeId,
                  }))}
                />
              ) : undefined
            ) : (
              !isEditing(updateCaseFields.EMPLOYEE) && employeeList.length ? (
                <EntityPopover
                  icon={<User width={16} />}
                  title="Employee"
                  allowCopy
                  items={employeeList.map(i => i.trim())}
                />
              ) : undefined
            )
          }
          isEditable={!isCreditStakeholderRevampEnabled}
          field={!isCreditStakeholderRevampEnabled ? updateCaseFields.EMPLOYEE : undefined}
        />
        <OverviewItem
          icon={<Users />}
          label="Parties Involved"
          value={isCreditStakeholderRevampEnabled ? stakeholderParties.map((p) => `${p.name} (${p.relatedAs})`).join(", ") : partyDisplayItems.join(", ")}
          inputError={partiesInvolvedError}
          popoverContent={
            !isEditingParties && (isCreditStakeholderRevampEnabled ? stakeholderParties.length > 0 : !!partiesInvolvedString) ? (
              isCreditStakeholderRevampEnabled ? (
                <EntityPopover
                  icon={<Users width={16} />}
                  title="Parties Involved"
                  items={stakeholderParties.map((party, index) => ({
                    content: (
                      <EntityPopoverRowWithBadge
                        title={party.name}
                        badge={party.relatedAs}
                        secondaryText={party.loanReferences}
                        showDivider={index > 0}
                      />
                    ),
                  }))}
                />
              ) : (
                <EntityPopover
                  icon={<Users width={16} />}
                  title="Parties Involved"
                  items={partyDisplayItems}
                />
              )
            ) : undefined
          }
          isEditable
          field={updateCaseFields.PARTIES_INVOLVED}
        />

      </div>

      {isEditing(updateCaseFields.LOAN_PRODUCT_INFO) && (
          <CreditAccountsModal
            onClose={() => dispatch(cancelEditMode())}
            onBack={() => dispatch(cancelEditMode())}
          />
      )}

      {isEditing(updateCaseFields.ACCOUNTS) && isCyberOpsRevampEnabled && (
          <AccountsModal
            onClose={() => dispatch(cancelEditMode())}
            onBack={() => dispatch(cancelEditMode())}
          />
      )}

      {!isCreditStakeholderRevampEnabled && isEditing(updateCaseFields.EMPLOYEE) && (
        <AddEmployeeModal
          onClose={() => dispatch(cancelEditMode())}
          onBack={() => dispatch(cancelEditMode())}
        />
      )}

      {editableField === updateCaseFields.PARTIES_INVOLVED  && (
        isCreditStakeholderRevampEnabled ? (
          <AddExistingStakeholderAsParty
            onClose={() => {
              setIsEditingParties(false);
              dispatch(cancelEditMode());
            }}
            onBack={() => {
              setIsEditingParties(false);
              dispatch(cancelEditMode());
            }}
          />
        ) : (
          <AddPartyModal
            onClose={() => {
              setIsEditingParties(false);
              dispatch(cancelEditMode());
            }}
            onBack={() => {
              setIsEditingParties(false);
              dispatch(cancelEditMode());
            }}
          />
        )
      )}

    </div>

  ), [summaryData,loanReferenceIds,accountReferenceIds,employeeList,partiesInvolvedString,partyDisplayItems,stakeholderEmployees,stakeholderParties,editableField,isSaving,isEditingParties,partiesInvolvedError,isCreditFraud,isCyberOpsRevampEnabled,isCreditStakeholderRevampEnabled,loanAccountInfo,caseAccounts,dispatch,employeeDisplayString]);

  return (
    <div className="space-y-3">
      {InvestigationSection}
      {AccountEmployeeSection}
      {BackgroundSection}
    </div>
  );
};